import { useState } from 'react'

import { useUser } from '@clerk/nextjs'
import { FormInstance, Input, message } from 'antd'

import { Form } from '@/components/shared-components/BUI/Form'
import { Text } from '@/components/shared-components/BUI/Text'

export enum PasswordField {
  CurrentPassword = 'currentPassword',
  NewPassword = 'newPassword',
  ConfirmNewPassword = 'confirmNewPassword',
}

export type PasswordFormFieldValues = {
  [PasswordField.CurrentPassword]: string
  [PasswordField.NewPassword]: string
  [PasswordField.ConfirmNewPassword]: string
}

export const PasswordUpdateContent: React.FC<{
  passwordForm: FormInstance
  onSuccess?: () => void
}> = ({ passwordForm, onSuccess }) => {
  const { user: clerkUser } = useUser()
  const [submitError, setSubmitError] = useState('')

  const onSubmitNewPassword = async (values: PasswordFormFieldValues) => {
    setSubmitError('')

    try {
      const updatePasswordResult = await clerkUser.updatePassword({
        ...(clerkUser.passwordEnabled && {
          currentPassword: values[PasswordField.CurrentPassword],
        }),
        newPassword: values[PasswordField.NewPassword],
      })

      if (!updatePasswordResult?.id) {
        throw new Error('Unexpected error updating password')
      }

      message.success('Password updated successfully')
      passwordForm.resetFields()
      // Emit an event or use a callback to close modal
      onSuccess?.()
    } catch (err) {
      setSubmitError(err.errors[0].longMessage)
      message.error('Password update failed')
    }
  }

  if (!clerkUser) {
    return null
  }

  return (
    <Form form={passwordForm} onFinish={onSubmitNewPassword}>
      {clerkUser.passwordEnabled && (
        <Form.Item
          name={PasswordField.CurrentPassword}
          label="Enter your current password"
          rules={[
            {
              required: true,
              message: 'Please enter your current password',
            },
          ]}
        >
          <Input.Password placeholder="Current password" />
        </Form.Item>
      )}

      <Form.Item
        name={PasswordField.NewPassword}
        label="Enter a new password"
        rules={[
          { required: true, message: 'Please enter a new password' },
          {
            min: 8,
            message: 'Your password must contain 8 or more characters.',
          },
        ]}
      >
        <Input.Password placeholder="New password" />
      </Form.Item>

      <Form.Item
        name={PasswordField.ConfirmNewPassword}
        label="Confirm your new password"
        rules={[
          {
            required: true,
            message: 'Please confirm your new password',
          },
          {
            validator: async (_, value) => {
              if (
                value !== passwordForm.getFieldValue(PasswordField.NewPassword)
              ) {
                return Promise.reject(new Error("Passwords don't match."))
              }
            },
          },
        ]}
      >
        <Input.Password placeholder="Confirm password" />
      </Form.Item>
      {submitError && (
        <Text semantic="errorDark" mt="1rem">
          {submitError}
        </Text>
      )}
    </Form>
  )
}
